/**
 *
 * UploadPage
 *
 */

import React, { useCallback } from 'react';
import { getBase64 } from 'lib/file';
import { useExpire } from 'lib/domonda-resource/react/hooks';
import { FormattedMessage } from 'lib/intl';
import { Link } from 'react-router-dom';


// api
import { getMandators } from 'lib/api/mandators';
import { getClients } from 'lib/api/clientData';
import { uploadInvoiceContainer } from 'lib/api/invoiceContainer';

// resource
import { useResolve } from 'lib/domonda-resource/react';

// ui
import {
  Box,
  Flex,
  Select,
  // TextField,
  Button,
  Err,
  ErrInline,
  SuccessInline,
} from '@domonda/ui';
import * as Icons from 'lib/domonda-icons';
import './decorate.css';

import { TextField } from '@material-ui/core';
// form
import {
  Form,
  FormSubmitHandler,
  FormSubmitErrorState,
  FormSubmittingState,
  FormSelectField,
  FormInputField,
  FormFileField,
} from '@domonda/react-form';

import { withStyles } from '@material-ui/core/styles';
import { CircularLoading } from 'modules/Loading/CircularLoading';
import { useIntl } from 'react-intl';

const CssTextField = withStyles({
  root: {
    '& label.Mui-focused': {
      color: 'rgb(59, 59, 59)',
    },
    '& .MuiInput-underline:after': {
      borderBottomColor: 'rgb(59, 59, 59);',
    },
    '& .MuiOutlinedInput-root': {
      '&.Mui-focused fieldset': {
        border: '1px solid rgb(59, 59, 59);',
      },
      '&.Mui-focused': {
        background: 'white',
      },
    },
  },
})(TextField);

interface FormValues {
  Name: string | null;
  Description: string | null;
  FileName: string | null;
  File: File | null;
  ApiIdentifier: string | null;
  Clients: string | null;
  ReceiverEmail: string | null;
  SenderEmail: string | null;
}

export interface UploadPageProps {}

export const UploadPage: React.FC<UploadPageProps> = () => {
  const [success, triggerSuccess] = useExpire(5000);

  const [mandators, error, retry] = useResolve(getMandators, {});
  const [clients, clientError, clientRetry] = useResolve(getClients, {});
  const { formatMessage } = useIntl();

  const handleSubmit = useCallback<FormSubmitHandler<FormValues>>(async (values) => {
    const { File, ApiIdentifier, Clients, ReceiverEmail, SenderEmail, ...rest } = values;

    if (!File) {
      throw new Error('Missing required fields!');
    }

    const fileBase64 = await getBase64(File, true);

    await uploadInvoiceContainer({
      ...rest,
      FileName: File.name,
      File: fileBase64,
      ApiIdentifier,
      AdditionalProperties: {
        ClientId: Clients,
        ReceiverEmail: ReceiverEmail,
        SenderEmail: SenderEmail,
      },
    });
    triggerSuccess();
  }, []);

  if (error) {
    return <Err error={error} onRetry={retry} />;
  }
  if (clientError) {
    return <Err error={clientError} onRetry={clientRetry} />;
  }

  if (!mandators || !clients) {
    return <CircularLoading />;
  }

  return (
      <div className='uploadContainer'>
        <div className="uploadSidebarMenu">
          <Link className='uploadSidebarItem' to="/">
            <Icons.Documents className='icon' />
            <FormattedMessage id="DOCUMENTS_DOCUMENTS" />
          </Link>
          <Link className='uploadSidebarItem' to="/dashboard" style={{display:'none'}}>
            <Icons.DashboardIcon className='icon' />
            Dashboard
          </Link>
        </div>
        <Box padding={5}>
          <Form<FormValues>
            defaultValues={{
              Name: null,
              Description: null,
              FileName: null,
              File: null,
              ApiIdentifier: mandators.Clients[0] ? mandators.Clients[0].Id : null,
              Clients: clients.Clients[0] ? clients.Clients[0].Id : null,
              ReceiverEmail: null,
              SenderEmail: null,
            }}
            onSubmit={handleSubmit}
            resetOnSuccessfulSubmit
          >
            <FormSubmittingState>
              {(submitting) => (
                <Flex container direction="column" spacing={1}>
                  <Flex item>
                    {!submitting && success && (
                      <SuccessInline message={formatMessage({ id: 'UPLOAD_SUCCESSFULL' })} />
                    )}
                  </Flex>

                  <Flex item>
                    <FormSubmitErrorState>
                      {(submitError, { resetSubmitError }) =>
                        submitError && <ErrInline error={submitError} onClose={resetSubmitError} />
                      }
                    </FormSubmitErrorState>
                  </Flex>

                  <Flex item>
                    <FormSelectField path="ApiIdentifier">
                      {({ selectProps }) => (
                        <Select
                          {...selectProps}
                          label={formatMessage({ id: 'SETTINGS_ClientName' })}
                          disabled={submitting}
                        >
                          <option value="">&mdash;</option>
                          {mandators.Clients.map(({ Id, Name }: { Id: any; Name: any }, index: any) => (
                            // TODO-db-190612 use just Identifier as key when BluDelta fixes it to being unique
                            //TODO: Wait for Mandator Api update
                            <option
                              key={`${Id}-${index.toString()}`}
                              value={/*Id*/ `Mandator${index + 1}`}
                            >
                              {Name}
                            </option>
                          ))}
                        </Select>
                      )}
                    </FormSelectField>
                  </Flex>

                  <Flex item container spacing={1}>
                    <Flex item flex={0.5}>
                      <FormInputField path="Name">
                        {({ inputProps }) => (
                          <CssTextField
                            {...inputProps}
                            label={formatMessage({ id: 'SETTINGS_Name' })}
                            color="primary"
                            variant="outlined"
                            style={{ width: '100%' }}
                            placeholder={formatMessage({ id: 'SETTINGS_Optional_name' })}
                            disabled={submitting}
                          />
                        )}
                      </FormInputField>
                    </Flex>

                    <Flex item flex={1}>
                      <FormInputField path="Description">
                        {({ inputProps }) => (
                          <CssTextField
                            {...inputProps}
                            label={formatMessage({ id: 'SETTINGS_Description' })}
                            variant="outlined"
                            style={{ width: '100%' }}
                            placeholder={formatMessage({ id: 'SETTINGS_Optional_description' })}
                            disabled={submitting}
                          />
                        )}
                      </FormInputField>
                    </Flex>
                  </Flex>
                  <Flex item>
                    <h3>{formatMessage({ id: 'INVOICE_DETAIL.AdditionalProperties' })}</h3>
                  </Flex>
                  <Flex item>
                    <FormSelectField path="Clients">
                      {({ selectProps }) => (
                        <Select
                          {...selectProps}
                          label={formatMessage({ id: 'INVOICE_DETAIL.Client' })}
                          disabled={submitting}
                        >
                          <option value="">&mdash;</option>
                          {clients.Clients.map(({ Id, Name }, index) => (
                            // TODO-db-190612 use just Identifier as key when BluDelta fixes it to being unique
                            //TODO: Wait for Mandator Api update
                            <option key={`${Id}-${index.toString()}`} value={Id}>
                              {Id} - {Name}
                            </option>
                          ))}
                        </Select>
                      )}
                    </FormSelectField>
                  </Flex>
                  <Flex item container spacing={1}>
                    <Flex item flex={1}>
                      <FormInputField path="ReceiverEmail">
                        {({ inputProps }) => (
                          <CssTextField
                            {...inputProps}
                            label={formatMessage({ id: 'INVOICE_DETAIL.ReceiverEmail' })}
                            variant="outlined"
                            style={{ width: '100%' }}
                            placeholder={formatMessage({ id: 'INVOICE_DETAIL.ReceiverEmail' })}
                            disabled={submitting}
                          />
                        )}
                      </FormInputField>
                    </Flex>
                    <Flex item flex={1}>
                      <FormInputField path="SenderEmail">
                        {({ inputProps }) => (
                          <CssTextField
                            {...inputProps}
                            label={formatMessage({ id: 'INVOICE_DETAIL.SenderEmail' })}
                            variant="outlined"
                            style={{ width: '100%' }}
                            placeholder={formatMessage({ id: 'INVOICE_DETAIL.SenderEmail' })}
                            disabled={submitting}
                          />
                        )}
                      </FormInputField>
                    </Flex>
                  </Flex>

                  <Flex item>
                    <FormFileField path="File" accept={['.pdf', '.doc']} required>
                      {({ inputProps }) => <input {...inputProps} disabled={submitting} />}
                    </FormFileField>
                  </Flex>

                  <Flex item container justify="flex-end">
                    <Flex item>
                      <Button variant="contained" type="submit" color="primary" disabled={submitting}>
                        {submitting
                          ? formatMessage({ id: 'SUBMITTING' })
                          : formatMessage({ id: 'SUBMIT' })}
                      </Button>
                    </Flex>
                  </Flex>
                </Flex>
              )}
            </FormSubmittingState>
          </Form>
        </Box>
      </div>

  );
};
